"use client"; // Error boundaries must be Client Components

import { useEffect, useState } from "react";
import { trackClientErrorBoundary } from "@drpcrd/shared/analytics/client";
import { createLinearErrorIssue } from "@drpcrd/shared/linear";
import { Button } from "@drpcrd/shared/ui";
import { Wrench } from "lucide-react";
import Link from "next/link";
import posthog from "posthog-js";

const logError = async (error: Error & { digest?: string }) => {
  // Get the current session replay URL
  const replayUrl = posthog.get_session_replay_url();
  const errorTitle = "Marketing site - Client Side Error";

  // Append things to the error
  const editedError = { ...error, errorTitle, replayUrl };

  // Logs to Vercel analytics
  trackClientErrorBoundary({
    userId: null,
    error,
    properties: {
      error_title: errorTitle,
      message: error.message,
      digest: error.digest ?? null,
      replay_url: replayUrl,
    },
  });

  // Logs to Linear if in production
  if (process.env.VERCEL_ENV === "production") {
    await createLinearErrorIssue(JSON.stringify(editedError));
  }
};

type Props = {
  error: Error & { digest?: string };
  reset: () => void;
};

const Error = ({ error, reset }: Props) => {
  const [digest, setDigest] = useState<string | undefined>();

  useEffect(() => {
    // We only want to log the error once
    if (digest == null) {
      setDigest(error.digest);
      logError(error);
    }
  }, [digest, error]);

  return (
    <main className="bg-primary-700 flex flex-1 flex-col items-center px-6 py-16">
      <div className="w-full max-w-128 space-y-6">
        <Wrench className="h-8 w-8 text-white" style={{ strokeWidth: 3 }} />
        <h1 className="font-poppins text-[72px] leading-[1.05] font-semibold tracking-[-0.04em]">
          <span className="text-white">Something broke.</span>
          <span className="text-primary-200"> Nutcake will fix it.</span>
        </h1>
        <div className="flex gap-3">
          <Button as={Link} href="/" variant="filled" size="md">
            Go to Nutcake.com
          </Button>
          <Button variant="outlined" size="md" onClick={reset}>
            Try again
          </Button>
        </div>
      </div>
    </main>
  );
};

export default Error;
